import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useId, useState } from 'react';

import s from './Tooltip.module.scss';

/**
 * A short label for something the interface has already drawn but not named —
 * the question-mark glyph on a file nothing can preview, an icon-only control.
 *
 * The app had none: 114 native `title` attributes stood in for it, which the
 * browser draws in its own chrome after about a second, ignores every token,
 * and never shows on keyboard focus. This shows on hover *and* focus, in the
 * app's own surface.
 *
 * It is a label, not a disclosure: anything a reader needs in order to act
 * belongs on the page. `aria-describedby` rather than a `role="tooltip"` on a
 * live region, so a screen reader reads the control and then its description
 * instead of announcing a floating panel.
 */
export const Tooltip = ({
  label,
  children,
  placement = 'top',
  block = false,
  multiline = false,
  className,
}: {
  label: string;
  /** The thing being described. Must accept a ref-free DOM node — an icon, a button. */
  children: ReactNode;
  placement?: 'top' | 'bottom';
  /**
   * Fill the box instead of shrinking to the glyph.
   *
   * The default suits what this was built for — an icon in a row of icons,
   * which sizes to itself. A described control that is laid out by its parent
   * (a grid column, a flex row that splits the width) has its width decided
   * elsewhere, and an inline-flex wrapper in the middle would hand it the
   * glyph's width instead of the column's.
   */
  block?: boolean;
  /**
   * Wrap the label onto several lines in a bubble of bounded width, for a sentence rather
   * than a name — the full reason a feature is locked, say.
   */
  multiline?: boolean;
  className?: string;
}) => {
  const id = useId();
  const [open, setOpen] = useState(false);

  return (
    <span
      className={clsx(s.wrap, block && s.wrapBlock, className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      /* Focus anywhere inside counts: the described thing is usually the
         focusable one, and the wrapper itself must not become a tab stop. */
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span
        aria-describedby={open ? id : undefined}
        className={clsx(s.anchor, block && s.anchorBlock)}
      >
        {children}
      </span>
      <span
        id={id}
        role="tooltip"
        /* Rendered always, hidden by opacity: mounting on hover would mean the
           description does not exist for assistive tech until a pointer finds
           it, which is the one reader who cannot use a pointer. */
        className={clsx(s.bubble, s[placement], multiline && s.multiline, open && s.open)}
      >
        {label}
      </span>
    </span>
  );
};
